import type { ImpactedAssetGroup } from "../../types/impactReport";

const INDUSTRY_TAXONOMY_PREFIX = "si.industry_taxonomy";
const TRANSITION_PREFIX = "si.industry_transition";
const REPORT_PREFIX = `${TRANSITION_PREFIX}.impact_report`;

/**
 * Centralized i18n key generators for the industry module's own surfaces. Copy that belongs to the demo
 * stays under `si.demo_mode.industry.*` — an opted-in account has no demo to describe.
 */
const industryI18nKeys = {
    launchBannerTitle: `${INDUSTRY_TAXONOMY_PREFIX}.launch_banner.title`,
    launchBannerDescription: `${INDUSTRY_TAXONOMY_PREFIX}.launch_banner.description`,
    launchBannerLearnMore: `${INDUSTRY_TAXONOMY_PREFIX}.launch_banner.learn_more`,
    launchBannerCta: `${INDUSTRY_TAXONOMY_PREFIX}.launch_banner.cta`,
    launchBannerClose: `${INDUSTRY_TAXONOMY_PREFIX}.launch_banner.close.tooltip`,
} as const;

export const industryTransitionKeys = {
    /** The modal footer's action. The banners use {@link bannerEnrollCta}, which reads shorter beside a demo CTA. */
    enrollCta: `${TRANSITION_PREFIX}.enroll.cta`,
    bannerEnrollCta: `${TRANSITION_PREFIX}.enroll.banner_cta`,

    reportTitle: `${REPORT_PREFIX}.title`,
    reportSubtitle: `${REPORT_PREFIX}.subtitle`,
    reportLearnMore: `${REPORT_PREFIX}.learn_more`,
    reportClose: `${REPORT_PREFIX}.close`,
    reportContactUs: `${REPORT_PREFIX}.contact_us`,
    reportContactUsToast: `${REPORT_PREFIX}.contact_us.toast`,
    reportCrmNotice: `${REPORT_PREFIX}.crm_notice`,

    reportTab: (group: ImpactedAssetGroup) => `${REPORT_PREFIX}.tab.${group}`,
    reportTabEmpty: (group: ImpactedAssetGroup) => `${REPORT_PREFIX}.tab.${group}.empty`,

    reportRowExpand: `${REPORT_PREFIX}.row.expand`,
    reportRowCollapse: `${REPORT_PREFIX}.row.collapse`,
    reportRowOwner: `${REPORT_PREFIX}.row.owner`,
    reportRowMoreCoverage: `${REPORT_PREFIX}.row.more_coverage`,
    reportRowMoreCoverageDescription: `${REPORT_PREFIX}.row.more_coverage.description`,
    reportRowSharperTargeting: `${REPORT_PREFIX}.row.sharper_targeting`,
    reportRowSharperTargetingDescription: `${REPORT_PREFIX}.row.sharper_targeting.description`,
    reportRowCompanies: `${REPORT_PREFIX}.row.companies`,
    reportRowCurrentIndustry: `${REPORT_PREFIX}.row.current_industry`,
    reportRowTaxonomy: `${REPORT_PREFIX}.row.taxonomy`,
    reportRowUnmapped: `${REPORT_PREFIX}.row.unmapped`,

    reportTotal: `${REPORT_PREFIX}.total`,
    reportLoadingTotal: `${REPORT_PREFIX}.total.loading`,
    reportNotNow: `${REPORT_PREFIX}.not_now`,

    loadErrorTitle: `${REPORT_PREFIX}.load_error.title`,
    loadErrorDescription: `${REPORT_PREFIX}.load_error.description`,
    retry: `${REPORT_PREFIX}.retry`,

    enrollingTitle: `${TRANSITION_PREFIX}.enrolling.title`,
    enrollingHint: `${TRANSITION_PREFIX}.enrolling.hint`,

    successTitle: `${TRANSITION_PREFIX}.success.title`,
    successDescription: `${TRANSITION_PREFIX}.success.description`,
    successCrmNotice: `${TRANSITION_PREFIX}.success.crm_notice`,
    successViewLeadGeneration: `${TRANSITION_PREFIX}.success.view_lead_generation`,
    successCrmSettings: `${TRANSITION_PREFIX}.success.crm_settings`,

    crmJobsBannerTitle: `${TRANSITION_PREFIX}.crm_jobs_banner.title`,
    crmJobsBannerDescription: `${TRANSITION_PREFIX}.crm_jobs_banner.description`,
    crmJobsBannerCta: `${TRANSITION_PREFIX}.crm_jobs_banner.cta`,

    crmManagementFieldMappingsBanner: `${TRANSITION_PREFIX}.crm_management_banner.field_mappings`,
    crmManagementEnrichmentJobsBanner: `${TRANSITION_PREFIX}.crm_management_banner.enrichment_jobs`,

    crmEnrichmentJobIndustryMappingTooltip: `${TRANSITION_PREFIX}.crm_enrichment_job.industry_mapping.tooltip`,

    enrollErrorTitle: `${TRANSITION_PREFIX}.enroll_error.title`,
    enrollErrorDescription: `${TRANSITION_PREFIX}.enroll_error.description`,
    enrollErrorDescriptionFinal: `${TRANSITION_PREFIX}.enroll_error.description.final`,

    announceLoading: `${REPORT_PREFIX}.announce.loading`,
    announceReady: `${REPORT_PREFIX}.announce.ready`,
    announceLoadError: `${REPORT_PREFIX}.announce.load_error`,
    announceEnrolling: `${TRANSITION_PREFIX}.announce.enrolling`,
    announceSuccess: `${TRANSITION_PREFIX}.announce.success`,
    announceEnrollError: `${TRANSITION_PREFIX}.announce.enroll_error`,
} as const;

export default industryI18nKeys;
